import { ArchiveItem, FilterCategory } from '../../shared/organisms/archive-section/archive-section';
import { resolveCardTeaser } from '../../core/utils/card-teaser.util';
import { resolveStrapiMediaUrl } from '../../core/utils/strapi-media.util';
import { formatEventDateRange, formatRecordedDate } from '../../core/utils/event-date.util';

// ---------------------------------------------------------------------------
// Tag extraction
// ---------------------------------------------------------------------------

/**
 * Extracts a filter tag value from a CPT item for a given key.
 *
 * - `'parent'`: reads `item.parent.title` (self-relation)
 * - `'industries'`/`'assetTypes'`: reads relation array, takes first item's title or name
 * - default (category, author, etc.): reads direct string field
 */
export function extractTagValue(item: Record<string, unknown>, key: string): string {
  if (key === 'parent') {
    const parent = item['parent'] as Record<string, unknown> | null;
    return (parent?.['title'] as string) ?? '';
  }

  if (key === 'faultType') {
    const ft = item['faultType'] as Record<string, unknown> | null;
    return (ft?.['name'] as string) ?? '';
  }

  if (key === 'industries' || key === 'assetTypes') {
    const relation = item[key] as Array<Record<string, unknown>> | null;
    if (!relation || relation.length === 0) return '';
    return (relation[0]['title'] as string) ?? (relation[0]['name'] as string) ?? '';
  }

  return (item[key] as string) ?? '';
}

// ---------------------------------------------------------------------------
// Date-based sorting
// ---------------------------------------------------------------------------

/**
 * Sorts raw Strapi items by effective date (originalDate → publishedAt)
 * in descending order (newest first). Returns a new array.
 */
export function sortByEffectiveDate(items: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...items].sort((a, b) => {
    const dateA = (a['originalDate'] as string) ?? (a['publishedAt'] as string) ?? '';
    const dateB = (b['originalDate'] as string) ?? (b['publishedAt'] as string) ?? '';
    return dateB.localeCompare(dateA);
  });
}

/** Use UTC calendar days, matching the event date formatter on server and client. */
function eventDay(value: unknown): string {
  if (typeof value !== 'string' || !value) return '';
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? '' : new Date(timestamp).toISOString().slice(0, 10);
}

function eventTiming(item: Record<string, unknown>, today: string) {
  const start = eventDay(item['startDate']);
  const rawEnd = eventDay(item['endDate']);
  const end = rawEnd && rawEnd >= start ? rawEnd : start;
  const past = Boolean(start && end < today);
  return {
    start,
    end,
    group: !start ? 2 : past ? 1 : 0,
    label: !start ? 'Date to be announced' : past ? 'Past event'
      : start <= today ? 'Happening now' : 'Upcoming event',
  };
}

/**
 * Current/upcoming events first (start ascending), then past events (end descending),
 * then undated events. An event stays current through its final calendar day.
 * Keep this shared entry point for both archive pages and embedded event archives.
 * Equal dates and undated events use title ascending; input is never mutated.
 */
export function sortByStartDateWithNullLast(
  items: Record<string, unknown>[],
  today = new Date().toISOString().slice(0, 10),
): Record<string, unknown>[] {
  return [...items].sort((a, b) => {
    const at = eventTiming(a, today);
    const bt = eventTiming(b, today);
    if (at.group !== bt.group) return at.group - bt.group;
    const dateOrder = at.group === 1
      ? bt.end.localeCompare(at.end) || bt.start.localeCompare(at.start)
      : at.start.localeCompare(bt.start);
    return dateOrder || ((a['title'] as string) ?? '').localeCompare((b['title'] as string) ?? '');
  });
}

/**
 * Sort webinar items by recordedDate DESCENDING (newest first). Items
 * without recordedDate sort to the BOTTOM of the output. Tiebreak by
 * title ascending for stability.
 *
 * Spec 36 line 99: "Webinars without recordedDate go to the bottom."
 *
 * Pitfall 19 — THE highest technical risk in Phase 45.
 * Strapi v5 emits raw SQL `ORDER BY recordedDate DESC` (no explicit
 * NULLS LAST clause). PostgreSQL DESC default is NULLS FIRST, so the
 * API response has null-recordedDate items at the TOP. This util is
 * the authoritative client-side reorder that flips them to the bottom.
 *
 * Mirrors sortByStartDateWithNullLast (event-page ASC) line-for-line
 * with three diffs:
 *   - field: startDate → recordedDate
 *   - direction: aDate.localeCompare(bDate) → bDate.localeCompare(aDate) (DESC)
 *   - null-position direction unchanged (-1 / +1 form is a contract
 *     about position, not direction)
 *
 * Scope: archive listing only. DO NOT call from related-webinars cross-
 * link sections — those preserve editor-curated relation order via the
 * dynamic-section.adapter.ts _resolvedRelation path (mirrors Pitfall 21
 * scope-guard from Phase 44).
 */
export function sortByRecordedDateDescNullLast(
  items: Record<string, unknown>[],
): Record<string, unknown>[] {
  return [...items].sort((a, b) => {
    const aDate = (a['recordedDate'] as string) || null;
    const bDate = (b['recordedDate'] as string) || null;
    if (aDate && !bDate) return -1;  // a has date, b doesn't → a first
    if (!aDate && bDate) return 1;   // b has date, a doesn't → b first
    if (aDate && bDate) {
      const cmp = bDate.localeCompare(aDate);  // DESC — operand order swapped
      if (cmp !== 0) return cmp;
    }
    // Both null OR equal → title-asc tiebreak.
    const aTitle = (a['title'] as string) ?? '';
    const bTitle = (b['title'] as string) ?? '';
    return aTitle.localeCompare(bTitle);
  });
}

// ---------------------------------------------------------------------------
// Archive item builder
// ---------------------------------------------------------------------------

/** CPTs that use `featuredImage` + `seo.metaDescription` instead of hero fields. */
const ARTICLE_FAMILY = new Set(['insight-page', 'news-page']);

/** CPTs that use `logo` (top-level media) + `cardDescription` for archive cards. */
const PARTNER_CONTENT_TYPES = new Set(['partner']);

/** CPTs that use `featuredImage` + event metadata (date + location). */
const EVENT_CONTENT_TYPES = new Set(['event-page']);

/** CPTs that use `featuredImage` + recordedDate decorator (Spec 36, Phase 45). */
const WEBINAR_CONTENT_TYPES = new Set(['webinar-page']);

/** CPTs that use top-level `cardImage` (square 1:1 thumbnail) for cards (Spec 28). */
const ASSET_TYPE_CONTENT_TYPES = new Set(['asset-type-page']);

/** CPTs where the card thumbnail comes from the first sections.hero-section's backgroundImage (spec 34). */
const LEAD_MAGNET_CONTENT_TYPES = new Set(['lead-magnet-page']);

/** Returns the appropriate card link text for a given content type. */
function linkTextForContentType(contentType: string): string {
  if (ARTICLE_FAMILY.has(contentType)) return 'Read more';
  if (contentType === 'case-study') return 'View case study';
  if (EVENT_CONTENT_TYPES.has(contentType)) return 'View event';
  if (WEBINAR_CONTENT_TYPES.has(contentType)) return 'Watch webinar';
  if (LEAD_MAGNET_CONTENT_TYPES.has(contentType)) return 'Download';
  // 'partner' and other CPTs fall through to 'Learn more'.
  return 'Learn more';
}

/** Joins city and country for the card meta line: 'City, Country' / 'City' / 'Country' / ''. */
function joinEventLocation(
  city: string | null | undefined,
  country: string | null | undefined,
): string {
  const c = (city ?? '').trim();
  const co = (country ?? '').trim();
  if (c && co) return `${c}, ${co}`;
  return c || co;
}

/**
 * Transforms raw CPT items into ArchiveItem[] for the archive section.
 */
export function buildArchiveItems(
  items: Record<string, unknown>[],
  archiveBasePath: string,
  archiveSlug: string,
  filterKeys: string[],
  strapiBaseUrl: string,
  contentType: string,
  today = new Date().toISOString().slice(0, 10),
): ArchiveItem[] {
  const isArticleFamily = ARTICLE_FAMILY.has(contentType);
  const isPartner = PARTNER_CONTENT_TYPES.has(contentType);
  const isEvent = EVENT_CONTENT_TYPES.has(contentType);
  const isWebinar = WEBINAR_CONTENT_TYPES.has(contentType);
  const isAssetType = ASSET_TYPE_CONTENT_TYPES.has(contentType);
  const isLeadMagnet = LEAD_MAGNET_CONTENT_TYPES.has(contentType);
  const linkText = linkTextForContentType(contentType);

  return items.map((item) => {
    const slug = item['slug'] as string;
    const title = (item['title'] as string) ?? '';

    // Body text: resolveCardTeaser → cardDescription → seo.metaDescription → null
    const hero = item['hero'] as Record<string, unknown> | null;
    const bodyText = resolveCardTeaser(
      item as { cardDescription?: string | null; seo?: { metaDescription?: string | null } | null },
    ) ?? '';

    // Build href — strip leading slashes from segments to avoid double-slash
    const segments = [archiveBasePath, archiveSlug, slug]
      .filter(Boolean)
      .map((s) => s.replace(/^\/+/, ''));
    const href = '/' + segments.join('/');

    // Build tags map
    const tags: Record<string, string> = {};
    for (const key of filterKeys) {
      tags[key] = extractTagValue(item, key);
    }

    // Build card image URL — article-family, event-page and webinar-page use
    // featuredImage, partners use top-level `logo` media, asset-type-page uses
    // top-level `cardImage` (Spec 28), others use hero.backgroundImage.
    // Width/height propagated from the source Strapi media object so
    // NgOptimizedImage has intrinsic dimensions (Plan 28-02 adapter sweep).
    //
    // WBNR-13: webinars MUST source from featuredImage (Spec 36 line 16). Webinars
    // have NO `hero` field — without `isWebinar` here, archive cards would fall
    // through to `hero.backgroundImage` and render imageless. Phase 45-03.
    let imageMedia: Record<string, unknown> | null;
    if (isArticleFamily || isEvent || isWebinar) {
      imageMedia = (item['featuredImage'] as Record<string, unknown> | null) ?? null;
    } else if (isPartner) {
      imageMedia = (item['logo'] as Record<string, unknown> | null) ?? null;
    } else if (isAssetType) {
      imageMedia = (item['cardImage'] as Record<string, unknown> | null) ?? null;
    } else if (isLeadMagnet) {
      // spec 34: lead-magnet-page has no top-level hero / featuredImage.
      // The card thumbnail derives from the first sections.hero-section's
      // backgroundImage (if present). Fallback: no image — the card renders
      // title + "Download" CTA only, per spec §Archive grid.
      const sections = (item['sections'] as Array<Record<string, unknown>> | null) ?? [];
      const firstHero = sections.find((s) => s['__component'] === 'sections.hero-section') ?? null;
      imageMedia = (firstHero?.['backgroundImage'] as Record<string, unknown> | null) ?? null;
    } else {
      imageMedia = (hero?.['backgroundImage'] as Record<string, unknown> | null) ?? null;
    }

    const imageUrl = (imageMedia?.['url'] as string) ?? null;
    const imageWidth = (imageMedia?.['width'] as number) ?? 0;
    const imageHeight = (imageMedia?.['height'] as number) ?? 0;
    const imageSrc = resolveStrapiMediaUrl(strapiBaseUrl, imageUrl) ?? '';
    // imageAlt fallback chain (quick-100 + Spec 28 §Card rendering contract):
    //   Strapi `featuredImage.alternativeText` (server-resolved by Phase 42 plan 42-04
    //   resolve-image-alt.js for case-study / event-page / asset-type-page / insight-page /
    //   news-page / industry-page) → empty string → fall back to the card `title`.
    //
    //   The title is already rendered inside the same anchor as the <h3 class="s__card-item__subhead">,
    //   so mirroring it into the alt attribute introduces zero net visual content but defuses
    //   Lighthouse "Image elements have [alt]" + "Links have a discernible name" on clickable
    //   image-variant cards (case-study, event-page, insight-page, news-page, industry-page,
    //   lead-magnet) where Strapi alt is missing/empty.
    //
    //   `||` (not `??`) is deliberate — empty-string and whitespace alt should both fall back.
    const imageAlt = ((imageMedia?.['alternativeText'] as string) || '').trim()
      || title;

    // Decorator label:
    //   - case-study  → first industry name
    //   - event-page  → meta line "<date> · <city, country>" (both segments optional)
    //     The decorator label slot mirrors the case-study pattern, keeping `body`
    //     free for the teaser so clickable cards retain a consistent visual rhythm.
    let decoratorValue = '';
    if (contentType === 'case-study') {
      decoratorValue = ((item['industries'] as Array<Record<string, unknown>>)?.[0]?.['title'] as string) ?? '';
    } else if (isEvent) {
      const dateLine = formatEventDateRange(
        item['startDate'] as string | null,
        item['endDate'] as string | null,
      );
      const locationLine = joinEventLocation(
        item['locationCity'] as string | null,
        item['locationCountry'] as string | null,
      );
      decoratorValue = [dateLine, locationLine].filter(Boolean).join(' \u00b7 ');
    } else if (isWebinar) {
      // Spec 36 \u2014 webinar archive card decorator is the recorded month/year only
      // (no location segment; webinars are virtual). formatRecordedDate returns
      // '' for null/undefined recordedDate, so the decorator drops cleanly.
      decoratorValue = formatRecordedDate(item['recordedDate'] as string | null);
    } else if (isLeadMagnet) {
      // spec 34: badge derives from the first hero-section's `overline`
      // (e.g. "Whitepaper", "Guide", "Framework"). Missing -> badge omitted.
      const sections = (item['sections'] as Array<Record<string, unknown>> | null) ?? [];
      const firstHero = sections.find((s) => s['__component'] === 'sections.hero-section') ?? null;
      decoratorValue = ((firstHero?.['overline'] as string) ?? '').trim();
    }
    const decorator = decoratorValue
      ? { type: 'label' as const, value: decoratorValue }
      : undefined;

    const variant = isAssetType ? ('thumb-image' as const) : ('image' as const);
    const cardData = {
      variant,
      subhead: title,
      ...(bodyText ? { body: bodyText } : {}),
      imageSrc,
      imageAlt,
      imageWidth,
      imageHeight,
      decorator,
      ...(isEvent ? { topRightLabel: eventTiming(item, today).label } : {}),
      link: { text: linkText, href, style: 'arrow' as const },
    };

    return { title, excerpt: bodyText, href, tags, cardData };
  });
}

// ---------------------------------------------------------------------------
// Filter category enrichment
// ---------------------------------------------------------------------------

/**
 * Derives filter options from live item data.
 * For each category, collects unique non-empty tag values from all items.
 */
export function enrichFilterCategories(
  categories: Array<{ key: string; label: string }>,
  items: ArchiveItem[],
): FilterCategory[] {
  return categories.map((cat) => {
    const uniqueValues = new Set<string>();
    for (const item of items) {
      const value = item.tags[cat.key];
      if (value) {
        uniqueValues.add(value);
      }
    }
    const options = Array.from(uniqueValues).sort();
    return { key: cat.key, label: cat.label, options };
  });
}
